import TagOutlinedIcon from '@mui/icons-material/TagOutlined'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Popover, { PopoverOrigin, PopoverProps } from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import { darken, getContrastRatio, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'

import LiquidGlassPaper from 'components/route-manager/RouteManagerMapV2/control-panel/LiquidGlassPaper'
import MeasureChip from 'containers/new-route-manager/RouteCard/MeasureChip'
import { COMMON } from 'theme/colors'

import type { ContainerInfoChip } from './useContainerInfoFields'
import type { Theme } from '@mui/material/styles'

type ContainerInfoPopoverProps = {
  open?: boolean
  x?: number
  y?: number
  title?: string
  serial?: string
  lines?: string[]
  chips?: ContainerInfoChip[]
  onClose?: () => void
}

// Shared container hover card. Anchors to the cursor's screen coords (the same
// `anchorReference="anchorPosition"` approach StopDetailsPopoverV2 uses) so it
// positions correctly over WebGL/canvas maps. Mirrors the stop popover's layout:
// no field labels — a title, muted secondary lines, and chips for descriptors.
const ANCHOR_ORIGIN: PopoverOrigin = { vertical: 'top', horizontal: 'right' }
// Below the cursor: anchored above it the card sat on the pin being dragged.
const TRANSFORM_ORIGIN: PopoverOrigin = { vertical: 'top', horizontal: -20 }
const PAPER_SLOT_PROPS = { opacity: 0.72, blur: 18, sx: { maxWidth: 320 } } as PopoverProps['slotProps']
const getPopoverSx = (theme: Theme) => ({ pointerEvents: 'none', zIndex: theme.zIndex.tooltip })
// Darken a low-contrast (e.g. white) chip colour so it stays legible on the glass card.
const getChipSx = (color?: string) => {
  if (!color) return undefined
  let safe = color
  try {
    if (getContrastRatio(color, COMMON.white) < 2.5) safe = darken(color, 0.4)
  } catch {
    safe = color
  }
  return { color: safe, borderColor: safe }
}

const ContainerInfoPopover = ({
  open = false,
  x,
  y,
  title = '',
  serial = '',
  lines = [],
  chips = [],
  onClose,
}: ContainerInfoPopoverProps) => {
  const theme = useTheme()
  if (!open || !title || !Number.isFinite(x) || !Number.isFinite(y)) return null

  const visibleLines = lines.filter(Boolean)
  const visibleChips = chips.filter(chip => chip && chip.label)

  return (
    <Popover
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      disableScrollLock
      hideBackdrop
      open
      anchorOrigin={ANCHOR_ORIGIN}
      anchorPosition={{ top: y as number, left: x as number }}
      anchorReference="anchorPosition"
      slotProps={{ paper: PAPER_SLOT_PROPS }}
      slots={{ paper: LiquidGlassPaper }}
      sx={getPopoverSx(theme)}
      transformOrigin={TRANSFORM_ORIGIN}
      onClose={onClose}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography noWrap color="text.primary" fontWeight={600} variant="subtitle1">
          {title}
        </Typography>
        {serial && (
          <Stack alignItems="center" direction="row" spacing={0.5}>
            <TagOutlinedIcon color="action" fontSize="small" />
            <Typography noWrap color="text.secondary" variant="body2">
              {serial}
            </Typography>
          </Stack>
        )}
        {visibleLines.map((line, index) => (
          // eslint-disable-next-line react/no-array-index-key -- static, fixed-order list; labels can repeat so position is the only stable key
          <Typography key={index} noWrap color="text.secondary" variant="body2">
            {line}
          </Typography>
        ))}
        {visibleChips.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
            {visibleChips.map((chip, index) =>
              chip.measureUnit ? (
                // eslint-disable-next-line react/no-array-index-key -- static, fixed-order list; chip labels can repeat
                <MeasureChip key={index} label={chip.label} measureUnit={chip.measureUnit} variant="outlined" />
              ) : (
                // eslint-disable-next-line react/no-array-index-key -- static, fixed-order list; chip labels can repeat
                <Chip key={index} label={chip.label} size="small" sx={getChipSx(chip.color)} variant="outlined" />
              )
            )}
          </Stack>
        )}
      </Box>
    </Popover>
  )
}

export default ContainerInfoPopover
